import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOPageMeta from '@/components/feature/SEOPageMeta';
import { brandInfo, testimonials } from '@/mocks/tvshows';

export default function AboutPage() {
  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于久播影院',
    description: brandInfo.description,
    url: 'https://www.jiubocinema.com/about',
    about: {
      '@type': 'Organization',
      name: brandInfo.name,
      description: brandInfo.description,
      foundingDate: brandInfo.founded,
      slogan: brandInfo.slogan,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: brandInfo.contact.email,
        availableLanguage: 'Chinese',
      },
    },
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: brandInfo.name,
    url: 'https://www.jiubocinema.com/',
    description: brandInfo.description,
    foundingDate: brandInfo.founded,
    slogan: brandInfo.slogan,
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: brandInfo.contact.email,
      availableLanguage: ['Chinese'],
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '12860',
      bestRating: '5',
    },
  };

  return (
    <div className="w-full min-h-screen bg-background-50">
      <SEOPageMeta
        title="关于我们"
        description="了解久播影院品牌故事、用户评价和联系方式。国内领先的高清电视剧免费在线观看平台。"
        keywords="关于久播影院,品牌介绍,用户评价,联系我们"
        canonicalUrl="https://www.jiubocinema.com/about"
      />
      <script type="application/ld+json">
        {JSON.stringify(aboutJsonLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(orgJsonLd)}
      </script>
      <div className="pt-16">
        <div className="max-w-screen-2xl mx-auto">
          <Breadcrumb
            items={[{ label: '关于我们' }]}
          />
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于久播影院
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto">
              {brandInfo.slogan}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
              {brandInfo.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center bg-background-100 rounded-lg p-5 md:p-6"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="max-w-3xl mx-auto">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                {brandInfo.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">我们的使命</h3>
                  <p className="text-sm text-foreground-600">{brandInfo.mission}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground-800 mb-2 font-heading">我们的愿景</h3>
                  <p className="text-sm text-foreground-600">{brandInfo.vision}</p>
                </div>
              </div>

              <h3 className="text-sm font-semibold text-foreground-800 mb-3 font-heading">核心价值观</h3>
              <div className="flex items-center gap-3 flex-wrap mb-8">
                {brandInfo.values.map((v) => (
                  <span
                    key={v}
                    className="px-4 py-2 bg-primary-50 text-primary-700 text-sm rounded-full whitespace-nowrap"
                  >
                    {v}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-6 text-center">
              用户评价
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5">
                  <div className="flex items-center gap-1 mb-2">
                    {Array.from({ length: t.rating }, (_, j) => (
                      <i key={j} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    &ldquo;{t.comment}&rdquo;
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                        <span className="text-xs font-medium text-secondary-700">
                          {t.name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-foreground-800">{t.name}</p>
                        <p className="text-[10px] text-foreground-500">{t.location}</p>
                      </div>
                    </div>
                    <span className="text-[10px] text-foreground-400">{t.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 mb-8">
              有任何问题或建议？欢迎随时联系我们
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-background-100 rounded-lg p-5">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-50">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1 font-heading">电子邮箱</h3>
                <p className="text-xs text-foreground-600">{brandInfo.contact.email}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-50">
                  <i className="ri-wechat-line text-lg text-accent-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1 font-heading">微信公众号</h3>
                <p className="text-xs text-foreground-600">{brandInfo.contact.wechat}</p>
              </div>
              <div className="bg-background-100 rounded-lg p-5">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-50">
                  <i className="ri-time-line text-lg text-secondary-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-1 font-heading">工作时间</h3>
                <p className="text-xs text-foreground-600">{brandInfo.contact.workingHours}</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}